/**
 * Fetching an image that needs a token.
 *
 * `photo_uri` holds a key — `photo/<club>/<uuid>.jpg` — and the bytes
 * come from `/v1/media/<key>`, which is authenticated like every other
 * `/v1` route. An `<img src>` cannot fetch that: an image tag sends no
 * Authorization header, and the alternative — a signed URL in a query
 * string — is a credential that ends up in a browser history, a referrer
 * and a proxy log, for a photograph.
 *
 * So the bytes are fetched here and turned into an object URL.
 *
 * ---------------------------------------------------------------------
 * WHY THE CACHE
 *
 * The food and beverage terminal draws the same member's face on the
 * check, in the seat list and in the household strip; the deck sheet
 * draws forty faces and half of them are one family. Without a cache
 * that is forty requests for eleven images, every render.
 *
 * Keyed by the key, holding the in-flight promise rather than the
 * result, so ten components mounting in the same tick share one request
 * rather than starting ten and keeping the last.
 *
 * Never evicted, and that is deliberate rather than an oversight: the
 * entries are object URLs of a few tens of kilobytes each, a session
 * looks at a few hundred faces at the outside, and a cache that revoked
 * a URL another component was still using would produce a broken image
 * with no way to reproduce it.
 */

import { getToken } from './api.ts';

const cache = new Map<string, Promise<string | null>>();

/**
 * An absolute URL is somebody else's photo service and is used as-is.
 * A club that already has one keeps it, which is the escape hatch the
 * photo endpoints keep too.
 */
function isExternal(uri: string): boolean {
  return /^(https?:)?\/\//i.test(uri) || uri.startsWith('data:');
}

export function resolvePhoto(uri: string | null | undefined): Promise<string | null> {
  if (!uri) return Promise.resolve(null);
  if (isExternal(uri)) return Promise.resolve(uri);

  const existing = cache.get(uri);
  if (existing) return existing;

  const pending = (async () => {
    try {
      const res = await fetch(`/v1/media/${uri}`, {
        headers: { authorization: `Bearer ${getToken() ?? ''}` },
      });
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      // A photograph that will not load is a photograph that is not
      // there, as far as anything on screen is concerned — Face already
      // falls back to initials and does not care which happened.
      return null;
    }
  })();

  // A failed fetch is not cached: the network comes back, and a member
  // whose face failed to load once should not be initials for the rest
  // of the session.
  void pending.then((r) => {
    if (r === null) cache.delete(uri);
  });

  cache.set(uri, pending);
  return pending;
}
